import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { queryClient } from '@/shared/services/queryClient'
import userService from '@/features/onboarding/services/userService'
import type { UserResponse } from '@/features/onboarding/types'

export const userKeys = {
  all: ['user'] as const,
  detail: (userId: string) => [...userKeys.all, userId] as const,
}

// Mirrors backend OnboardUserUseCase.isProfileComplete
export const isProfileComplete = (user: UserResponse | null | undefined): boolean =>
  user?.profileMetadata != null

/**
 * Store a freshly fetched/updated user (e.g. from onboard or a profile update)
 * so every useCurrentUser consumer re-renders without a refetch.
 */
export function cacheCurrentUser(user: UserResponse) {
  const { userId, setUserId } = useAuthStore.getState()
  // A different account signed in without a logout: drop the previous user's cache
  if (userId && userId !== user.userId) {
    queryClient.clear()
  }
  setUserId(user.userId)
  queryClient.setQueryData(userKeys.detail(user.userId), user)
}

/**
 * Refetch the current user's profile from the server (e.g. after a payment).
 * Resolves once active observers have the new data.
 */
export function refreshCurrentUser() {
  const userId = useAuthStore.getState().userId
  if (!userId) return Promise.resolve()
  return queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) })
}

/**
 * Current user's profile, fetched from GET /api/v1/users/{userId}.
 *
 * If the userId is unknown (onboard failed at login), it is resolved once via
 * the idempotent onboard endpoint and cached under the real key.
 */
export function useCurrentUser() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.userId)

  const query = useQuery({
    queryKey: userId ? userKeys.detail(userId) : [...userKeys.all, 'bootstrap'],
    queryFn: async () => {
      if (!userId) {
        const response = await userService.onboardUser()
        cacheCurrentUser(response.data)
        return response.data
      }
      const response = await userService.getUserById(userId)
      return response.data
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
  })

  const user = query.data ?? null

  return {
    user,
    profileComplete: isProfileComplete(user),
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
